// src/pages/credits/CreditsPurchaseReturnPage.jsx
// Landing page after Yoco redirects back — handles success / cancel / failure.
// On success, polls Firestore until credits increase, then shows confirmation.
import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../../services/firebase";
import { useAuth } from "../../hooks/useAuth";
import AppLayout from "../../components/layout/AppLayout";
import Button from "../../components/ui/Button";
import styles from "./CreditsPurchaseReturnPage.module.css";

const BUNDLE_NAMES   = { starter: "Starter", pro: "Pro", business: "Power" };
const BUNDLE_CREDITS = { starter: 10, pro: 20, business: 60 };
const VERIFY_URL = 'https://us-central1-gigscout-90d97.cloudfunctions.net/verifyYocoPayment';

export default function CreditsPurchaseReturnPage() {
  const [searchParams] = useSearchParams();
  const navigate       = useNavigate();
  const { currentUser, refreshProfile } = useAuth();

  const cancelled = searchParams.get("cancelled") === "1";
  const failed    = searchParams.get("failed")    === "1";
  const bundleId  = searchParams.get("bundle");

  const [status,  setStatus]  = useState(cancelled ? "cancelled" : failed ? "failed" : "verifying");
  const [attempt, setAttempt] = useState(0);
  const creditsBefore = useRef(Number(sessionStorage.getItem("creditsBefore") ?? 0));
  const verified      = useRef(false);
  const MAX_POLLS     = 15;

  // Fallback: call our Cloud Function to verify with Yoco directly and credit if paid
  const runVerification = async () => {
    if (verified.current || !currentUser) return;
    verified.current = true;
    const checkoutId = sessionStorage.getItem("checkoutId");
    if (!checkoutId) { setStatus("pending"); return; }
    try {
      const token = await currentUser.getIdToken();
      const resp  = await fetch(VERIFY_URL, {
        method:  'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body:    JSON.stringify({ checkoutId }),
      });
      const result = await resp.json();
      if (result.credited || result.alreadyProcessed) {
        await refreshProfile?.();
        sessionStorage.removeItem("creditsBefore");
        sessionStorage.removeItem("checkoutId");
        setStatus("success");
      } else {
        setStatus("pending");
      }
    } catch {
      setStatus("pending");
    }
  };

  useEffect(() => {
    if (status !== "verifying" || !currentUser) return;

    // Listen to the user's Firestore document — resolve as soon as credits increase
    const unsub = onSnapshot(doc(db, "users", currentUser.uid), async (snap) => {
      if (!snap.exists()) return;
      const newCredits = snap.data().credits ?? 0;
      setAttempt(a => {
        const next = a + 1;
        if (newCredits > creditsBefore.current) {
          setStatus("success");
          refreshProfile?.();
          sessionStorage.removeItem("creditsBefore");
          sessionStorage.removeItem("checkoutId");
        } else if (next >= MAX_POLLS) {
          // Webhook didn't fire in time — verify directly with Yoco
          runVerification();
        }
        return next;
      });
    });

    return () => unsub();
  }, [currentUser, status, refreshProfile]);

  return (
    <AppLayout>
      <div className={styles.page}>
        <div className={styles.card}>
          {status === "verifying" && (
            <>
              <div className={styles.spinner} />
              <h2 className={styles.heading}>Verifying payment…</h2>
              <p className={styles.sub}>
                Checking with Yoco ({attempt}/{MAX_POLLS})
              </p>
            </>
          )}

          {status === "success" && (
            <>
              <div className={styles.icon}>✅</div>
              <h2 className={styles.heading}>Payment successful!</h2>
              <p className={styles.sub}>
                {BUNDLE_CREDITS[bundleId] ?? ""} credits have been added to your account.
                {bundleId && ` (${BUNDLE_NAMES[bundleId]} bundle)`}
              </p>
              <Button onClick={() => navigate("/jobs")}>Find freelance jobs</Button>
            </>
          )}

          {status === "pending" && (
            <>
              <div className={styles.icon}>⏳</div>
              <h2 className={styles.heading}>Payment received</h2>
              <p className={styles.sub}>
                Your credits are being processed and will appear in your account within a few minutes.
              </p>
              <Button onClick={() => navigate("/credits")}>Go to Credits</Button>
            </>
          )}

          {status === "cancelled" && (
            <>
              <div className={styles.icon}>↩️</div>
              <h2 className={styles.heading}>Payment cancelled</h2>
              <p className={styles.sub}>No charge was made. You can try again whenever you're ready.</p>
              <Button onClick={() => navigate("/credits")}>Back to Credits</Button>
            </>
          )}

          {status === "failed" && (
            <>
              <div className={styles.icon}>❌</div>
              <h2 className={styles.heading}>Payment failed</h2>
              <p className={styles.sub}>Something went wrong with the payment. Please try again.</p>
              <Button onClick={() => navigate("/credits")}>Try again</Button>
            </>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
